/**
 * AI'ın ürettiği rapor dosyasının kartı ve önizlemesi.
 *
 * Rapor sohbet balonunun içine gömülmüyor: uzun bir belge, konuşmayı okunmaz hale
 * getiriyor. Kart raporun adını ve boyutunu gösteriyor; açınca tam ekran önizleme
 * geliyor (mobilde de tam ekran — rapor dar bir panelde okunmaz).
 *
 * İçerik açılınca gelir: sunucuda saklanan çıktı (kimlikli) ilk açılışta çekilir, satır içi
 * HTML (eski kayıt / canlı Write) hemen gösterilir. HTML iframe'de, PDF ve resim tarayıcı
 * görüntüleyicisinde; Excel/CSV gibi dosyalar yalnız indirilir.
 *
 * "Raporlara ekle": çıktı kullanıcının menüsüne (AI Raporları) kaydedilir; sohbet silinse
 * de kalır, bildirimi varsa parametre alıp yeniden çalıştırılır.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookmarkPlus, Download, FileSpreadsheet, FileText, Maximize2, Printer, SlidersHorizontal } from 'lucide-react';
import { useAuthStore } from '@/entities/session/model/authStore';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Dialog } from '@/shared/ui/Dialog';
import { Spinner } from '@/shared/ui/Spinner';
import { toast } from '@/shared/ui/toastStore';
import type { ReportArtifact } from '@/shared/types/aiChat';
import { fetchArtifactBlob, useSaveAiReport } from '../api/aiApi';
import { downloadBlob, isHtmlArtifact, isImageArtifact, isPdfArtifact, withExtension } from '../model/artifacts';
import { buildSrcDoc, printFrame, REPORT_SANDBOX } from '../model/reportFrame';

type Loaded = { kind: 'html'; html: string } | { kind: 'blob'; url: string; blob: Blob };

export function AiArtifactCard({ artifact }: { artifact: ReportArtifact }) {
  const { t } = useTranslation();
  const userId = useAuthStore((s) => s.user?.userId ?? null);
  const save = useSaveAiReport(userId);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);

  const html = isHtmlArtifact(artifact);
  const browserViewable = isPdfArtifact(artifact) || isImageArtifact(artifact);
  const spreadsheet = /\.(xlsx|xls|csv)$/i.test(artifact.fileName);
  const sizeKb = Math.max(1, Math.round((artifact.size ?? artifact.code?.length ?? 0) / 1024));

  const openPreview = () => {
    setOpen(true);
    if (loaded || loading) return;
    if (artifact.code) {
      setLoaded({ kind: 'html', html: artifact.code });
      return;
    }
    if (!artifact.artifactId) {
      setError(t('ai.artifact.loadFailed'));
      return;
    }
    setLoading(true);
    setError(null);
    fetchArtifactBlob(artifact.artifactId)
      .then(async (blob) => {
        if (html) setLoaded({ kind: 'html', html: await blob.text() });
        else setLoaded({ kind: 'blob', url: URL.createObjectURL(blob), blob });
      })
      .catch(() => setError(t('ai.artifact.loadFailed')))
      .finally(() => setLoading(false));
  };

  // Blob URL'si kart kalkınca bırakılır.
  useEffect(
    () => () => {
      if (loaded?.kind === 'blob') URL.revokeObjectURL(loaded.url);
    },
    [loaded],
  );

  const srcDoc = useMemo(() => (open && loaded?.kind === 'html' ? buildSrcDoc(loaded.html) : ''), [open, loaded]);

  const download = () => {
    const name = withExtension(artifact.fileName || 'rapor', html ? 'html' : null);
    if (loaded?.kind === 'html') downloadBlob(new Blob([loaded.html], { type: 'text/html;charset=utf-8' }), name);
    else if (loaded?.kind === 'blob') downloadBlob(loaded.blob, name);
  };

  const saveToMenu = () => {
    if (!artifact.artifactId) return;
    save.mutate(
      { artifactId: artifact.artifactId },
      {
        onSuccess: (report) => toast.success(t('ai.artifact.saved', { title: report.title })),
        onError: () => toast.error(t('ai.artifact.saveFailed')),
      },
    );
  };

  const Icon = spreadsheet ? FileSpreadsheet : FileText;

  return (
    <>
      <button
        type="button"
        onClick={openPreview}
        className="border-border bg-bg-elevated hover:border-brand-500/60 hover:bg-bg-hover mt-2 flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors"
      >
        <span className="bg-brand-500/10 text-brand-600 flex h-9 w-9 shrink-0 items-center justify-center rounded-md">
          <Icon className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{artifact.title}</span>
          <span className="text-fg-subtle flex items-center gap-1 text-xs">
            <span className="truncate">
              {t('ai.artifact.open')} · {sizeKb} KB
            </span>
            {artifact.hasParameters ? (
              <span className="border-border inline-flex shrink-0 items-center gap-0.5 rounded-full border px-1.5 text-[10px]">
                <SlidersHorizontal className="h-2.5 w-2.5" />
                {t('ai.artifact.parametric')}
              </span>
            ) : null}
          </span>
        </span>
        <Maximize2 className="text-fg-subtle h-4 w-4 shrink-0" />
      </button>

      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={artifact.title}
        size="full"
        fill
        footer={
          <>
            {html ? (
              <Button variant="secondary" size="sm" disabled={!loaded} onClick={() => printFrame(frameRef.current)}>
                <Printer className="h-4 w-4" />
                {t('ai.artifact.print')}
              </Button>
            ) : null}
            <Button variant="secondary" size="sm" disabled={!loaded} onClick={download}>
              <Download className="h-4 w-4" />
              {t('ai.artifact.download')}
            </Button>
            {artifact.artifactId && userId ? (
              <Button variant="secondary" size="sm" loading={save.isPending} onClick={saveToMenu}>
                <BookmarkPlus className="h-4 w-4" />
                {t('ai.artifact.save')}
              </Button>
            ) : null}
            <Button size="sm" onClick={() => setOpen(false)}>
              {t('common:Close')}
            </Button>
          </>
        }
      >
        {error ? (
          <Alert tone="danger">{error}</Alert>
        ) : loading || !loaded ? (
          <div className="flex h-full min-h-[40vh] items-center justify-center">
            <Spinner className="h-6 w-6" />
          </div>
        ) : loaded.kind === 'html' ? (
          <iframe
            ref={frameRef}
            title={artifact.title}
            srcDoc={srcDoc}
            // allow-same-origin YOK: rapor opak kaynakta kalır, uygulamaya erişemez.
            sandbox={REPORT_SANDBOX}
            referrerPolicy="no-referrer"
            className="border-border bg-bg h-full min-h-[60vh] w-full rounded-md border"
          />
        ) : browserViewable ? (
          <iframe
            title={artifact.title}
            src={loaded.url}
            referrerPolicy="no-referrer"
            className="border-border bg-bg h-full min-h-[60vh] w-full rounded-md border"
          />
        ) : (
          <div className="text-fg-muted flex h-full min-h-[40vh] flex-col items-center justify-center gap-3 text-sm">
            <Icon className="h-10 w-10" />
            <p>{t('ai.artifact.noPreview')}</p>
            <Button size="sm" onClick={download}>
              <Download className="h-4 w-4" />
              {t('ai.artifact.download')}
            </Button>
          </div>
        )}
      </Dialog>
    </>
  );
}
